import React from 'react';
import MuiTypography, { TypographyProps } from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { translateInline } from '../../i18n/inlineTranslations';

const catalogCache = new Map<string, Map<string, string>>();
const getCatalog = (i18n: any, language: string) => {
  if (catalogCache.has(language)) return catalogCache.get(language)!;
  const catalog = new Map<string, string>();
  const visit = (source: any, target: any) => Object.entries(source || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && typeof target?.[key] === 'string') catalog.set(value, target[key]);
    else if (value && typeof value === 'object') visit(value, target?.[key]);
  });
  visit(i18n.getResourceBundle('en', 'translation'), i18n.getResourceBundle(language, 'translation'));
  catalogCache.set(language, catalog);
  return catalog;
};

const localizeNode = (node: React.ReactNode, language: string, catalog: Map<string, string>): React.ReactNode => {
  if (typeof node === 'string') {
    const leading = node.match(/^\s*/)?.[0] || '';
    const trailing = node.match(/\s*$/)?.[0] || '';
    const source = node.trim();
    if (!source) return node;
    const inline = translateInline(language, source);
    return `${leading}${inline === source ? catalog.get(source) || source : inline}${trailing}`;
  }
  if (Array.isArray(node)) return node.map((item) => localizeNode(item, language, catalog));
  return node;
};

const LocalizedTypography = React.forwardRef<HTMLElement, TypographyProps>(function LocalizedTypography(
  { children, ...props },
  ref,
) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  return (
    <MuiTypography ref={ref} {...props}>
      {localizeNode(children, language, getCatalog(i18n, language))}
    </MuiTypography>
  );
}) as typeof MuiTypography;

export default LocalizedTypography;
